import React, { useState, useEffect, useRef } from 'react';
import { useFocusTrap } from '../../utils/focusTrap';
import styles from './LocalVersionsSection.module.css';

interface CreateLocalVersionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: { description?: string }) => Promise<{ success: boolean; error?: string }>;
}

export const CreateLocalVersionDialog: React.FC<CreateLocalVersionDialogProps> = ({
  isOpen,
  onClose,
  onCreate,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useFocusTrap(modalRef, isOpen);

  useEffect(() => {
    if (isOpen) {
      setDescription('');
      setError('');
      setIsSubmitting(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          onClose();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSubmitting(true);
    setError('');

    const result = await onCreate({
      description: description.trim() || undefined,
    });

    if (result.success) {
      onClose();
    } else {
      setError(result.error || 'Failed to create local version');
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={styles.modalOverlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-local-version-title"
    >
      <div ref={modalRef} className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 id="create-local-version-title" className={styles.modalTitle}>
          Create Local Version
        </h2>
        <p className={styles.modalSubtitle}>
          Local versions are for development. They generate browser and worker tokens for testing without uploading files.
        </p>

        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="description" className={styles.label}>
              Description (Optional)
            </label>
            <textarea
              ref={inputRef}
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={styles.textarea}
              placeholder="e.g., Local development environment for testing new features"
              disabled={isSubmitting}
            />
            <div className={styles.helpText}>
              A unique version ID will be generated automatically.
            </div>
          </div>

          {error && <div className={styles.errorText}>{error}</div>}

          <div className={styles.modalActions}>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className={`${styles.buttonSmall} ${styles.buttonSecondary}`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={styles.button}
            >
              {isSubmitting ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
